import React, { useEffect, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import BtnSave from "../Commons/Buttons/BtnSave";
import { Checkbox, Input, Button } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AntDTable from "../AntDTable/AntDTable";
import {
  getPastMedicalHistory,
  createPastMedicalHistory,
  deletePastMedicalHistory,
} from "../Helpers/API/MedicalHistoryApi/MedicalHistoryApi";

function PastMedicalHistory({ onHistoryChange }) {
  const id = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [existingRecords, setExistingRecords] = useState([]);
  const [detailsMap, setDetailsMap] = useState({});

  const DISEASES = [
    "None",
    "Cerebrovascular Disease",
    "Coronary Artery Disease",
    "Epilepsy/Seizure Disorder",
    "Hepatitis",
    "Hyperlipidemia",
    "Peptic Ulcer",
    "Pneumonia",
    "Thyroid Disease",
    "Extrapulmonary Tuberculosis",
    "Urinary Tract Infection",
    "Mental Illness",
  ];

  async function fetchExisting() {
    setLoading(true);
    const res = await getPastMedicalHistory({ patient_id: id.id });
    let records = [];
    if (res.data) {
      records = res.data.data || [];
      setExistingRecords(records);
    } else {
      setExistingRecords([]);
    }
    setSelected([]);
    setDetailsMap({});
    setLoading(false);
    return records;
  }

  function onToggle(name, checked) {
    setSelected((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(name);
        if (name !== "None") {
          // Initialize details if not present
          setDetailsMap((prevDetails) => {
            if (!prevDetails[name] || prevDetails[name].length === 0) {
              return { ...prevDetails, [name]: [""] };
            }
            return prevDetails;
          });
        }
      } else {
        set.delete(name);
      }
      return Array.from(set);
    });
  }

  function updateDetail(disease, index, value) {
    setDetailsMap((prev) => {
      const list = prev[disease] ? [...prev[disease]] : [];
      list[index] = value;
      return { ...prev, [disease]: list };
    });
  }

  function addDetail(disease) {
    setDetailsMap((prev) => {
      const list = prev[disease] ? [...prev[disease]] : [];
      list.push("");
      return { ...prev, [disease]: list };
    });
  }

  function removeDetail(disease, index) {
    setDetailsMap((prev) => {
      const list = prev[disease] ? [...prev[disease]] : [];
      list.splice(index, 1);
      return { ...prev, [disease]: list };
    });
  }

  async function onSubmit() {
    setSaving(true);
    const selectedNames = new Set(selected);

    const toDelete = existingRecords.filter(
      (r) => DISEASES.includes(r.disease) && !selectedNames.has(r.disease)
    );

    let errors = [];
    const namesToCreate = [];
    const descriptionsToCreate = [];
    for (const rec of toDelete) {
      const resp = await deletePastMedicalHistory(rec.id);
      if (resp.error) {
        errors.push(resp.error?.data?.messages?.error_messages?.[0] || rec.name);
      }
    }
    for (const disease of Array.from(selectedNames)) {
      const details = detailsMap[disease] || [];
      const existingDetailRecords = existingRecords.filter(
        (r) => r.disease === disease
      );
      // Delete all existing details for this disease
      for (const rec of existingDetailRecords) {
        const resp = await deletePastMedicalHistory(rec.id);
        if (resp.error) {
          errors.push(
            resp.error?.data?.messages?.error_messages?.[0] || rec.name
          );
        }
      }
      if (disease === "None") {
        namesToCreate.push(disease);
        descriptionsToCreate.push("");
        continue;
      }
      // Accumulate new details
      for (const d of details) {
        if (d && d.trim()) {
          namesToCreate.push(disease);
          descriptionsToCreate.push(d.trim());
        }
      }
    }
    if (namesToCreate.length > 0) {
      const resp = await createPastMedicalHistory(
        { disease: namesToCreate, description: descriptionsToCreate },
        id
      );
      if (resp?.error) {
        errors.push("Failed to save past medical history records");
      }
    }
    if (errors.length) {
      toast.error("Some records failed to save");
    } else {
      toast.success("Past Medical History saved");
    }
    await fetchExisting();
    if (onHistoryChange) onHistoryChange();
    setSaving(false);
  }

  useEffect(() => {
    fetchExisting().then((records) => {
      if (records?.length > 0 && onHistoryChange) onHistoryChange();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id.id]);

  return (
    <div className="card-with-table p-3">
      <div className="row mb-3">
        <Form.Label className="form-title align-left">
          Past Medical History
        </Form.Label>
      </div>

      <Row className="pl-4 pt-2">
        <Col sm={12}>
          <div className="d-flex flex-column gap-2">
            {DISEASES.map((d) => (
              <div key={d} className="d-flex flex-column gap-2 mb-2">
                <Checkbox
                  checked={selected.includes(d)}
                  disabled={loading || saving}
                  onChange={(e) => onToggle(d, e.target.checked)}
                >
                  {d}
                </Checkbox>
                {d !== "None" && selected.includes(d) && (
                  <div className="pl-4 mt-1 d-flex flex-column gap-2">
                    {((detailsMap[d] && detailsMap[d].length > 0)
                      ? detailsMap[d]
                      : [""]
                    ).map((detail, idx, arr) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center gap-2"
                      >
                        <Input
                          placeholder="Enter details"
                          value={detail}
                          onChange={(e) => updateDetail(d, idx, e.target.value)}
                          style={{ maxWidth: 320 }}
                          disabled={loading || saving}
                        />
                        {arr.length > 1 && (
                          <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            onClick={() => removeDetail(d, idx)}
                            disabled={loading || saving}
                          />
                        )}
                        {idx === arr.length - 1 && (
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="small"
                            onClick={() => addDetail(d)}
                            disabled={loading || saving}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Col>
      </Row>

      <div className="row mt-4">
        <div className="col-sm-4 mb-2 show-summarry-all-buttons">
          <BtnSave
            label={"Save"}
            onClick={onSubmit}
            isClicked={saving}
            disabled={loading}
          />
        </div>
      </div>
      <hr />
      <div className="row justify-content-left">
        <div className="col-sm-12 overflow">
          <AntDTable
            tableHeaders={["NAME", "DESCRIPTION", "ADDED ON"]}
            headerSelector={["disease", "description", "added_on"]}
            tableData={existingRecords}
            loading={loading || saving}
            isClickable={false}
            withAction={false}
            scroll={{ x: true }}
          />
        </div>
      </div>
    </div>
  );
}

export default PastMedicalHistory;
